// src/sim/catch/constants.ts — P3 catch/escort tunable constants.
//
// Invariants:
//   - No side effects; module-level numeric constants only.
//   - Tick-count or pixel-based; no milliseconds.
//   - Mark P7-tunable values with `// TODO(P7 POL-06): retune from playtest`.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-04, D-11, D-14.

/** D-11: extra slack added to (hunter.r + hider.r) for the catch circle. 16 px ≈ ½ tile. TODO(P7 POL-06): retune from playtest. */
export const P3_CATCH_SLACK_PX = 16;

/** D-04: caught hider moves at hunter speed × this multiplier (0.9). TODO(P7 POL-06): retune from playtest. */
export const P3_ESCORT_SPEED_MULTIPLIER = 0.9;

/** D-04: caught hider target offset = 1 tile behind hunter facing (32 px at current tileWidth). */
export const P3_ESCORT_OFFSET_TILES = 1;

/**
 * D-14: re-catch immunity window after free-all, in ticks.
 *
 * Original spec: 2 ticks (~67 ms). Debug session cascade-prevention
 * (2026-04-28) demonstrated that 2 ticks is far too short in steady state:
 * the hunter parked at the plinth post-drop-off can re-catch the freshly-
 * freed hider 67 ms later, the freed hider hasn't even started walking
 * away, and the catch+drop+free-all loop sustains a ~2-tick cadence
 * regardless of the AI-side liberate-suppression window. Bumped to 30
 * ticks (1 s at 30 Hz) so the freed hider has time to walk ~3 tiles away
 * AND the hunter's recentDropOffTick patrol-base penalty has time to
 * route them to a far-quarter patrol point. TODO(P7 POL-06): retune from
 * playtest if 1 s feels too generous to the freed hider.
 */
export const P3_FREED_IMMUNITY_TICKS = 90;

/**
 * Debug session cascade-prevention (2026-04-28): the plinth's central ring
 * radius in pixels. The ring sits at zoneCenter('plinth') and is SOLID —
 * no character (hunter/hider/dog/adult) may occupy any pixel inside the
 * ring. Liberators trigger free-all by TOUCHING the ring boundary (their
 * character circle in contact with the ring). Captured hiders are parked
 * at the ring boundary (distance == ringRadius + characterRadius from
 * center) so they form a halo around the outside.
 *
 * 48 px ≈ 3× the player diameter (P1_CHARACTER_RADIUS = 16, diameter = 32,
 * 3× diameter = 96, ring diameter = 96 → ring radius = 48). This is small
 * enough that a liberator must walk DELIBERATELY to it (replacing the prior
 * polygon-zone "step inside" semantics that produced the cascade) and large
 * enough to render as a visible game object on the map.
 */
export const P3_PLINTH_RING_RADIUS_PX = 48;

/**
 * Tolerance for the ring-contact trigger. A liberator's center must be
 * within (ringRadius + charRadius + this slack) of the ring center to
 * qualify. Originally 16 px (½ player diameter) — user feedback
 * 2026-04-30: that allowed hiders walking PAST the ring to incidentally
 * trigger free-all. Tightened to 4 px (just enough for sub-pixel jitter
 * from the wall clamp) so the trigger requires actual contact.
 */
export const P3_PLINTH_RING_CONTACT_SLACK_PX = 4;

/**
 * Debug free-all-misfire-frozen-hider FLEE-FROM-BASE (2026-06-12). MECHANIC 1.
 *
 * Distance (px) from the plinth ring centre at which a freed captive / the
 * liberator is considered "clear of the base" and STOPS the full-speed radial
 * flee, handing control back to the normal hider BT (which then re-hides via the
 * BUG#2c reachable-spot pick). 128 px ~= 2x the ring-contact distance (ring
 * radius 48 + char radius 16 = 64), i.e. roughly 4 tiles out from the centre, far
 * enough that the freed hider is genuinely off the ring halo. TODO(P7 POL-06):
 * retune from playtest.
 */
export const P3_FLEE_FROM_BASE_CLEAR_PX = 128;

/**
 * Debug free-all-misfire-frozen-hider FLEE-FROM-BASE (2026-06-12). MECHANIC 1.
 *
 * Hard cap (ticks) on the radial flee state, in case a freed hider is walled off
 * and cannot reach P3_FLEE_FROM_BASE_CLEAR_PX. 60 ticks ~= 2 s at 30 Hz. The flee
 * ends at min(reached-clear-distance, this cap), whichever comes first, after
 * which normal hider AI resumes. TODO(P7 POL-06): retune from playtest.
 */
export const P3_FLEE_FROM_BASE_MAX_TICKS = 60;

/**
 * Debug free-all-misfire-frozen-hider CATCH->FREE COOLDOWN (2026-06-12). MECHANIC 2.
 *
 * Minimum age (ticks) a deposited captive must reach before a free-all may free
 * it. applyFreeAll excludes any caught-at-base hider whose
 * (currentTick - caughtAtTick) < this value. Prevents the instant re-free that
 * sustained the catch/free thrash loop at the base. 60 ticks ~= 2 s at 30 Hz.
 * After the cooldown elapses the captive is freeable normally (never permanently
 * blocked). TODO(P7 POL-06): retune from playtest.
 */
export const P3_CATCH_FREE_COOLDOWN_TICKS = 60;


